import _ from 'lodash'
import React from 'react'
import apis from 'browser/app/models/apis'
import { Entity } from 'shared-libs/models/entity'
import type { AppointmentFilter } from 'shared-libs/generated/server-types/entity/yms/scheduler/schedulerRuleEntity'
import { HelpBlock } from 'browser/components/atomic-elements/atoms/help-block/help-block'
import { SelectField } from 'browser/components/atomic-elements/molecules/fields/select-field'
import { LabelFormGroup } from 'browser/components/atomic-elements/molecules/label-form-group/label-form-group'
import { JSONSelectFactory } from 'browser/components/json-elements/higher-order-components/json-select-factory'
import { EntitySelectField } from 'browser/components/atomic-elements/molecules/fields/select-field/entity-select-field'

const UIEntitySelectField = JSONSelectFactory(EntitySelectField)
const UISelectField = JSONSelectFactory(SelectField)

interface IRuleSelectorProps {
  entity?: Entity
  onChange?: (value: any) => void
  value: any
}

interface IRuleSelectorState {
  selectedTemplateId: string | null
  isUnrecognized?: boolean
}

/**
 * Allows the user to select from pre-defined schedulerRule templates and input values for their
 * template parameters to construct a schedulerRule.
 */
export class RuleTemplateSelector extends React.PureComponent<
  IRuleSelectorProps,
  IRuleSelectorState
> {
  constructor(props: IRuleSelectorProps) {
    super(props)

    const { value } = props

    let selectedTemplateId = value?.templateId

    // TODO: remove later on, this was just for backward compatibility with older rules during
    // development, prior to adding the templateId to the saved object.
    // HACK: try to find the templateId by matching the JSON shape.
    if (_.isNil(selectedTemplateId) && !_.isNil(value)) {
      const pathSequence = value.pathSequence

      for (const template of PRECANNED_FILTER_TEMPLATES) {
        const templateValue = template.template(null)
        const templatePathSequence = templateValue.pathSequence

        if (_.isEqual(pathSequence, templatePathSequence)) {
          selectedTemplateId = template.id
          break
        }
      }
    }

    this.state = {
      selectedTemplateId: selectedTemplateId ?? null,
      isUnrecognized: !_.isEmpty(value) && _.isNil(selectedTemplateId),
    }
  }

  public async componentDidMount(): Promise<void> {
    const { value } = this.props
    const { selectedTemplateId } = this.state
    const entityIds = value?.values ?? []

    if (entityIds.length && (selectedTemplateId === 'customer' || selectedTemplateId === 'carrier')) {
      await apis.getStore().getOrFetchRecords(entityIds)
      this.forceUpdate()
    }
  }

  public componentDidUpdate(prevProps: Readonly<IRuleSelectorProps>): void {
    if (!_.isEqual(prevProps.value, this.props.value)) {
      if (_.isNil(this.props.value)) {
        this.setState({
          selectedTemplateId: null,
        })
      } else {
        this.setState({
          selectedTemplateId: this.props.value.templateId,
        })
      }
    }
  }

  public render() {
    const { value } = this.props
    const { selectedTemplateId, isUnrecognized } = this.state

    if (isUnrecognized) {
      // TODO: later, if we get rid of templates, we should be get rid of this and render a more
      // generic UI for editing the appointment filter. For now, this makes unrecognized filters
      // read-only and displays their json content, since we have no pretty way to display this.
      return (
        <LabelFormGroup label="Unrecognized Template">
          <HelpBlock helpText={JSON.stringify(value, null, 2)} style={{
            whiteSpace: 'pre'
          }}/>
        </LabelFormGroup>
      )
    }

    const templateOptions = PRECANNED_FILTER_TEMPLATES.map((template) => ({
      label: template.displayName,
      value: template.id,
    }))

    return (
      <div>
        <UISelectField
          label="Filter Template"
          enumOptions={templateOptions}
          optionLabelPath="label"
          optionValuePath="value"
          value={selectedTemplateId}
          onChange={this.handleTemplateChange}
        />
        {this.renderTemplateInput()}
      </div>
    )
  }

  private renderTemplateInput() {
    const { value } = this.props
    const { selectedTemplateId } = this.state

    if (!selectedTemplateId) {
      return null
    }

    const renderFn = TEMPLATE_COMPONENT_MAP[selectedTemplateId]
    if (!renderFn) {
      return null
    }

    // don't carry over stale values from the previously committed template
    const currentValue = (value?.templateId === selectedTemplateId) ? (value?.values ?? null) : null
    const hydratedValue = this.hydrateFilterValue(currentValue)

    return renderFn(hydratedValue, this.handleFilterValueChange)
  }

  private handleTemplateChange = (templateId: string) => {
    this.setState({
      selectedTemplateId: templateId,
    })
  }

  private handleFilterValueChange = (value: any) => {
    const { entity, onChange, value: currentValue } = this.props
    const { selectedTemplateId } = this.state

    if (!selectedTemplateId || !entity) {
      return
    }

    const template = PRECANNED_FILTER_TEMPLATES.find((t) => t.id === selectedTemplateId)
    if (!template) {
      return
    }

    const filterValue = this.extractFilterValue(value)
    if (!filterValue?.length) {
      return
    }

    const appointmentFilter = {
      ...template.template(filterValue),
      templateId: selectedTemplateId,
      shouldNegate: currentValue?.shouldNegate,
    }

    onChange?.(appointmentFilter)
  }

  private extractFilterValue(value: any): any {
    const { selectedTemplateId } = this.state

    if (selectedTemplateId === 'customer' || selectedTemplateId === 'carrier') {
      return _.castArray(value).map((v) => v?.entityId ?? v).filter(Boolean)
    }

    return _.castArray(value).filter(Boolean)
  }

  private hydrateFilterValue(values: string[] | null): any {
    const { selectedTemplateId } = this.state

    if (selectedTemplateId === 'customer' || selectedTemplateId === 'carrier') {
      return _.castArray(values).filter(Boolean).map((id) => {
        const entity = apis.getStore().getRecord(id)
        return { entityId: id, displayName: entity?.displayName }
      })
    }

    return values
  }
}

interface IFilterTemplate {
  id: string
  displayName: string
  template: (value: any) => AppointmentFilter
}

/**
 * Predefined filter templates that generate appointmentFilter configurations for a
 * schedulerRule.json.
 */
const PRECANNED_FILTER_TEMPLATES: IFilterTemplate[] = [
  {
    id: 'customer',
    displayName: 'Filter by Customer',
    template: (customerEntityIds: string[]) => ({
      pathSequence: [
        {
          path: 'core_yms_carrierAppointment.freightLoads[*]',
          entityType: '/1.0/entities/metadata/core_yms_carrierAppointment.json',
        },
        {
          path: 'core_yms_freightLoad.customer',
          entityType: '/1.0/entities/metadata/core_yms_freightLoad.json',
        },
      ],
      shouldNegate: false,
      values: customerEntityIds,
    }),
  },
  {
    id: 'carrier',
    displayName: 'Filter by Carrier',
    template: (carrierEntityIds: string[]) => ({
      pathSequence: [
        {
          path: 'core_yms_carrierAppointment.freightLoads[*]',
          entityType: '/1.0/entities/metadata/core_yms_carrierAppointment.json',
        },
        {
          path: 'core_yms_freightLoad.carrier',
          entityType: '/1.0/entities/metadata/core_yms_freightLoad.json',
        },
      ],
      shouldNegate: false,
      values: carrierEntityIds,
    }),
  },
  {
    id: 'inbound_outbound',
    displayName: 'Filter by Inbound/Outbound',
    template: (direction: Array<'Inbound' | 'Outbound'>) => ({
      pathSequence: [
        {
          path: 'core_yms_carrierAppointment.freightLoads[*]',
          entityType: '/1.0/entities/metadata/core_yms_carrierAppointment.json',
        },
        {
          path: 'core_yms_freightLoad.direction',
          entityType: '/1.0/entities/metadata/core_yms_freightLoad.json',
        },
      ],
      shouldNegate: false,
      values: direction,
    }),
  },
  {
    id: 'load_type',
    displayName: 'Filter by Live/Drop Load Type',
    template: (loadTypes: Array<'Live' | 'Drop & Hook'>) => ({
      pathSequence: [
        {
          path: 'core_yms_carrierAppointment.freightLoads[*]',
          entityType: '/1.0/entities/metadata/core_yms_carrierAppointment.json',
        },
        {
          path: 'core_yms_freightLoad.type',
          entityType: '/1.0/entities/metadata/core_yms_freightLoad.json',
        },
      ],
      shouldNegate: false,
      values: loadTypes,
    }),
  },
  {
    id: 'pallet_type',
    displayName: 'Filter by Load Configuration',
    template: (palletTypes: Array<"Pallet Loaded" | "Slip Sheet" | "Floor Loaded" | "Restack">) => ({
      pathSequence: [
        {
          path: 'core_yms_carrierAppointment.palletType',
          entityType: '/1.0/entities/metadata/core_yms_carrierAppointment.json',
        },
      ],
      shouldNegate: false,
      values: palletTypes,
    }),
  },
]

/**
 * Maps predefined template IDs to render functions that return input components for collecting
 * filter values.
 */
const TEMPLATE_COMPONENT_MAP = {
  customer: (value, onChange) => (
    <UIEntitySelectField
      key="customer-select"
      entityType="/1.0/entities/metadata/customer.json"
      label="Customer"
      multi={true}
      value={value}
      onChange={onChange}
    />
  ),
  carrier: (value, onChange) => (
    <UIEntitySelectField
      key="carrier-select"
      entityType="/1.0/entities/metadata/carrier.json"
      label="Carrier"
      multi={true}
      value={value}
      onChange={onChange}
    />
  ),
  inbound_outbound: (value, onChange) => (
    <UISelectField
      label="Appointment Type"
      enumOptions={[
        { label: 'Inbound', value: 'Inbound' },
        { label: 'Outbound', value: 'Outbound' },
      ]}
      optionLabelPath="label"
      optionValuePath="value"
      multi={true}
      value={value}
      onChange={onChange}
    />
  ),
  load_type: (value, onChange) => (
    <UISelectField
      label="Load Type"
      enumOptions={[
        { label: 'Live', value: 'Live' },
        { label: 'Drop & Hook', value: 'Drop & Hook' },
      ]}
      optionLabelPath="label"
      optionValuePath="value"
      multi={true}
      value={value}
      onChange={onChange}
    />
  ),
  pallet_type: (value, onChange) => (
    <UISelectField
      label="Load Configuration"
      enumOptions={[
        { label: 'Pallet Loaded', value: 'Pallet Loaded' },
        { label: "Slip Sheet", value: "Slip Sheet" },
        { label: "Floor Loaded", value: "Floor Loaded" },
        { label: "Restack", value: "Restack" },
      ]}
      optionLabelPath="label"
      optionValuePath="value"
      multi={true}
      value={value}
      onChange={onChange}
    />
  ),
}
